import { Quest, select } from "gamedata/GameData";
import { ReactNode, useState } from "react";
import { Link, useLoaderData } from "react-router";
import { z } from "zod";
import { ControlBox } from "~/components/form/ControlBox";
import { Input } from "~/components/form/Input";
import { MultiSelect } from "~/components/form/MultiSelect";
import { QuestLocationList } from "~/components/quest/QuestLocationList";
import { QuestProfList } from "~/components/quest/QuestProfList";
import { QuestRaceList } from "~/components/quest/QuestRaceList";
import { QuestRewardList } from "~/components/quest/QuestRewardList";
import { QuestStartNPCList } from "~/components/quest/QuestStartNPCList";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Button } from "~/components/ui/Button";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { GameRaceUname, RACE_NAMES } from "~/types";
import { gameName } from "~/util/common";
import { createFiltering } from "~/util/Filtering";
import { lstring, Realm, tt, useRealm, useT, useTurl } from "~/util/Lang";
import { applyPaging, Pager } from "~/util/Listing";
import type { MetaFunc, OurLoaderArgs } from "~/util/Loaders";
import { createLoader } from "~/util/Loaders";
import { createSorting, SortColumnName, useUrlSorting } from "~/util/Sorting";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";
import {
  urlQueryFromSearchParams,
  useSetUrlQuery,
  useUrlQuery,
} from "~/util/UrlUtils";

type QuestFilter = {
  minLevel: null | number;
  maxLevel: null | number;
  race: readonly string[];
};

type Config = {
  noArmorType?: boolean;
  noWeaponType?: boolean;
  filter?: (item: Quest) => boolean;
};

const PageQuery = z.object({
  p: z.optional(z.string()),
  s: z.optional(z.string()),
});

const SORTER = createSorting<Quest>({
  lvl: (a, b) => (a.minLevel ?? 0) - (b.minLevel ?? 0),
  loc: (a, b) => {
    return (a.startNpcs[0]?.locs[0]?.name.en ?? "").localeCompare(
      b.startNpcs[0]?.locs[0]?.name.en ?? ""
    );
  },
});

const FILTER = createFiltering<QuestFilter, Quest>({
  pack: (f) => ({
    min: f.minLevel != null ? f.minLevel.toString() : undefined,
    max: f.maxLevel != null ? f.maxLevel.toString() : undefined,
    race: f.race.join(","),
  }),
  unpack: (r) => {
    return {
      minLevel: r.min ? parseInt(r.min) : null,
      maxLevel: r.max ? parseInt(r.max) : null,
      race: r.race ? r.race.split(",") : [],
    };
  },
  filter: (item, f) => {
    if (typeof f.minLevel === "number" && (item.minLevel ?? 0) < f.minLevel) {
      return false;
    }
    if (typeof f.maxLevel === "number" && (item.minLevel ?? 0) > f.maxLevel) {
      return false;
    }
    if (
      f.race.length > 0 &&
      item.requirements.races.filter((r) => f.race.includes(r.raceName))
        .length === 0
    ) {
      return false;
    }

    return true;
  },
});

async function makeData(la: OurLoaderArgs) {
  // Log
  logPageView(la);
  //
  const t = tt.bind(null, la.realm.lang);

  const query = PageQuery.parse(
    Object.fromEntries(la.url.searchParams.entries())
  );

  const filter = FILTER.fromUrlQuery(
    urlQueryFromSearchParams(la.url.searchParams)
  );

  const all = Array.from(la.gd.quests.values());
  const sorted = SORTER.apply(all, query.s);
  const filtered = FILTER.apply(sorted, filter);

  const [paged, paging] = applyPaging(filtered, query);

  const quests = select(paged, {
    select: {
      id: true,
      uname: true,
      name: t,
      minLevel: true,
      maxLevel: true,
      requirements: {
        category: true,
        items: { select: { select: { name: t } } },
        occupation: {
          select: { id: true, profName: true, name: t },
        },
        races: {
          select: {
            raceName: true,
            icon: true,
            name: t,
          },
        },
      },
      reward: { exp: true, sp: true, items: { select: { icon: true } } },
      startNpcs: {
        select: {
          id: true,
          npcName: true,
          name: t,
          level: true,
          nick: t,
          nickColor: true,
          locs: {
            select: { name: t, locName: true },
          },
        },
      },
    },
  });

  return { quests, paging, authUser: la.req.sess?.user };
}

export const loader = createLoader(async (la) => {
  return makeData(la);
});

type LoaderData = Awaited<ReturnType<typeof makeData>>;

export const meta: MetaFunc<LoaderData> = (cx) => {
  return {
    title: `${cx.t({ en: "Quests", ru: "Квесты" })} - ${homePage(cx.realm)}`,
    description: getDesc(cx),
    ogDescription: getDesc(cx),
    ogTitle: getOgTitle(cx),
  };
};

export default function Quests() {
  const { quests, paging } = useLoaderData() as LoaderData;
  const t = useT();
  const turl = useTurl();
  const questsTitle = t({ en: "Quests", ru: "Квесты" });
  const pager = <Pager paging={paging} />;
  const urlSorting = useUrlSorting();

  return (
    <div>
      <div className="content">
        <Breadcrumb items={[{ title: questsTitle, url: turl("/quests") }]} />
        <PageTitle>{questsTitle}</PageTitle>
        <Filters />
      </div>
      {pager}
      <div style={{ overflowX: "auto" }}>
        <Table
          data={quests}
          id={(quest) => quest.id}
          cols={[
            {
              title: t({ en: "Name", ru: "Имя" }),
              cell: (quest) => (
                <Link to={turl(`/quests/${quest.uname}`)}>{quest.name}</Link>
              ),
            },
            {
              title: (
                <SortColumnName name="lvl" by={urlSorting}>
                  {t({ en: "Level", ru: "Уровень" })}
                </SortColumnName>
              ),
              cell: (quest) => (
                <>
                  {quest.minLevel || ""}
                  {quest.minLevel && quest.maxLevel ? "-" : null}
                  {quest.maxLevel || ""}
                </>
              ),
            },
            {
              title: t({ en: "Reward", ru: "Награда" }),
              cell: (quest) => <QuestRewardList reward={quest.reward} />,
            },
            {
              title: t({ en: "Race", ru: "Раса" }),
              cell: (quest) => (
                <QuestRaceList races={quest.requirements.races} />
              ),
            },
            {
              title: t({ en: "Class", ru: "Класс" }),
              cell: (quest) => (
                <QuestProfList profs={quest.requirements.occupation} />
              ),
            },
            {
              title: t({ en: "Start NPC", ru: "Стартовый НПЦ" }),
              cell: (quest) => (
                <QuestStartNPCList startNpcs={quest.startNpcs} />
              ),
            },
            {
              title: (
                <SortColumnName name="loc" by={urlSorting}>
                  {t({ en: "Location", ru: "Локация" })}
                </SortColumnName>
              ),
              cell: (quest) => (
                <QuestLocationList startNpcs={quest.startNpcs} />
              ),
            },
          ]}
        />
      </div>
      {pager}
    </div>
  );
}

function getDesc(cx: { realm: Realm; t: (str: lstring) => string }) {
  return cx.t({
    en: `A complete searchable and filterable list of all Quests in ${gameName(
      cx.realm.game
    )}.`,
    ru: `Полный список квестов ${gameName(
      cx.realm.game
    )} с пошаговым прохождением, точным расположением ключевых НПЦ и точным списком наград.`,
  });
}

function getOgTitle(cx: { realm: Realm; t: (str: lstring) => string }) {
  return cx.t({
    en: `All quests ${gameName(cx.realm.game)}`,
    ru: `Все квесты ${gameName(cx.realm.game)}`,
  });
}

function Filters(props: { pages?: ReactNode }) {
  const t = useT();
  const { game } = useRealm();
  const urlQuery = useUrlQuery();
  const setUrlQuery = useSetUrlQuery();

  const [filter, setFilter] = useState(() => FILTER.fromUrlQuery(urlQuery));

  return (
    <div style={{ padding: "0 5px" }}>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <div>
          <ControlBox
            title="Min level"
            reset={() => setFilter((f) => ({ ...f, minLevel: null }))}
          >
            <Input
              value={filter.minLevel == null ? "" : filter.minLevel.toString()}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  minLevel: parseInt(e.target.value) || null,
                }))
              }
            />
          </ControlBox>
          <div style={{ height: 10 }} />
          <ControlBox
            title="Max level"
            reset={() => setFilter((f) => ({ ...f, maxLevel: null }))}
          >
            <Input
              value={filter.maxLevel == null ? "" : filter.maxLevel.toString()}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  maxLevel: parseInt(e.target.value) || null,
                }))
              }
            />
          </ControlBox>
        </div>
        <div>
          <ControlBox
            title="Race"
            reset={() => setFilter((f) => ({ ...f, race: [] }))}
          >
            <MultiSelect
              options={GameRaceUname.options.map((wt) => ({
                value: wt,
                title: t(RACE_NAMES[wt]),
              }))}
              value={filter.race}
              onChange={(wt) => setFilter((f) => ({ ...f, race: wt }))}
            />
          </ControlBox>
        </div>
      </div>

      <div
        style={{
          alignItems: "flex-end",
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 20,
        }}
      >
        <div style={{ padding: "0 0 8px" }}>
          <Button
            onClick={() => {
              setUrlQuery(
                { ...urlQuery, f: FILTER.encode(filter) },
                { preventScrollReset: true }
              );
            }}
          >
            Apply
          </Button>
        </div>
        {props.pages}
      </div>
    </div>
  );
}
